import { useId, useState } from 'react';

import type { AssetResponse, AssetUploadMetadata } from '../../api';
import {
  DERIVED_FROM_SOURCE,
  METADATA_FIELD_LABELS,
  type MetadataFieldKey,
  type MetadataFormValue,
} from '../../constants/metadataFields';
import {
  ASSET_RULES,
  CAROUSEL_FORMAT,
  intersectOwnedFields,
  isPublishRequired,
  type AssetRules,
} from '../../constants/assetTypes';
import {
  CHANNEL_OPTIONS,
  OLA_LANGUAGE,
  OLA_MARKET,
  PLATFORM_RIGHTS_OPTIONS,
  UNKNOWN_VALUES,
  filmDurationLabel,
  formatDateDDMMYYYY,
  type TaxonomyOption,
} from '../../constants/taxonomy';
import { useVocabulary, vocabularyLabel, vocabularyLabels } from '../../vocabulary/useVocabulary';
import { CopyElementsEditor } from './CopyElementsEditor';
import { MultiSelectField } from './MultiSelectField';
import { useCampaignSuggestions } from './useCampaignSuggestions';

/**
 * The whole new-spec metadata field set as one controlled component.
 *
 * Shared between the upload modal's Details step and the drawer's bulk editor
 * so the two can never drift: same fields, same vocabularies. The upload modal
 * passes no `mixed` set; the drawer derives one from the selection.
 *
 * Every option list the backend owns (nameplates, carline codes, business
 * units, tiers, AI indicators, agencies) is read from `useVocabulary`; nothing
 * here is hard-coded. Nameplate, carline code and business unit are three
 * independent multi-selects: nothing is pre-filled or suggested between them.
 *
 * Which fields show is decided by the kinds of asset being edited
 * (`assetTypes`): each kind's `ownedFields` in `constants/assetTypes.ts`, and
 * for a mixed selection the fields every selected kind carries.
 */

/**
 * The form value as the `POST /batches` metadata payload.
 *
 * Empty fields are omitted, and so are fields the kind does not own: the form
 * hides those rather than clearing them, so a copy element typed for a master
 * image before the kind was switched to source image must not travel with
 * the upload — the backend would refuse the whole batch over a field the user
 * can no longer see.
 */
export function toUploadMetadata(
  value: MetadataFormValue,
  assetType: AssetRules,
): AssetUploadMetadata {
  const owned = new Set<string>(ASSET_RULES[assetType].ownedFields);
  const payload = toMetadataPayload(value);
  return Object.fromEntries(Object.entries(payload).filter(([field]) => owned.has(field)));
}

function toMetadataPayload(value: MetadataFormValue): AssetUploadMetadata {
  const rows = value.copy_elements
    .map((row) => ({ type: row.type, value: row.value.trim() }))
    .filter((row) => row.value !== '');
  return {
    ...(value.nameplate.length > 0 && { nameplate: value.nameplate }),
    ...(value.business_unit.length > 0 && { business_unit: value.business_unit }),
    ...(value.carline_code.length > 0 && { carline_code: value.carline_code }),
    ...(value.tier.length > 0 && { tier: value.tier }),
    ...(value.ai_indicator && { ai_indicator: value.ai_indicator }),
    ...(value.source_agency && { source_agency: value.source_agency }),
    ...(value.domain && { domain: value.domain }),
    // Always sent, false included: a source image always has an answer.
    // `toUploadMetadata` drops it for every other kind.
    is_lifestyle_image: value.is_lifestyle_image,
    ...(value.channel_rights.length > 0 && { channel_rights: value.channel_rights }),
    ...(value.country_rights.length > 0 && { country_rights: value.country_rights }),
    ...(value.expiry_rights && { expiry_rights: value.expiry_rights }),
    ...(rows.length > 0 && { copy_elements: rows }),
    ...(value.platform_rights.length > 0 && { platform_rights: value.platform_rights }),
    ...(value.format_type && { format_type: value.format_type }),
    ...(value.format_size && { format_size: value.format_size }),
    ...(value.duration_seconds && { duration_seconds: Number(value.duration_seconds) }),
    ...(value.legacy_fcv_id.trim() && { legacy_fcv_id: value.legacy_fcv_id.trim() }),
    ...(value.campaign_name.trim() && { campaign_name: value.campaign_name.trim() }),
    ...(value.creative_variation.trim() && {
      creative_variation: value.creative_variation.trim(),
    }),
    ...(value.production_ticket_ref.trim() && {
      production_ticket_ref: value.production_ticket_ref.trim(),
    }),
    ...(value.briefing_form_ref.trim() && { briefing_form_ref: value.briefing_form_ref.trim() }),
    ...(value.notes.trim() && { notes: value.notes.trim() }),
  };
}

/** The upload payload without the fields the backend copies from a linked source image. */
export function withoutDerivedFields(metadata: AssetUploadMetadata): AssetUploadMetadata {
  const rest = { ...metadata };
  for (const field of DERIVED_FROM_SOURCE) delete rest[field];
  return rest;
}

/**
 * Channel rights with `channel` ticked. Unknown stands alone, as the server
 * insists: ticking it clears the channels, and ticking a channel clears it.
 */
export function withChannel(selected: string[], channel: string): string[] {
  const unknown = UNKNOWN_VALUES.channel_rights;
  if (channel === unknown) return [unknown];
  return [...selected.filter((entry) => entry !== unknown), channel];
}

/**
 * The form value with every "Unknown" answer taken out. Unknown is a source
 * image's to pick, so the upload modal drops it when the kind switches away
 * from source images: kept, it would travel with an upload the server refuses.
 */
export function withoutUnknown(value: MetadataFormValue): MetadataFormValue {
  return {
    ...value,
    business_unit: value.business_unit.filter((unit) => unit !== UNKNOWN_VALUES.business_unit),
    channel_rights: value.channel_rights.filter(
      (channel) => channel !== UNKNOWN_VALUES.channel_rights,
    ),
    source_agency: value.source_agency === UNKNOWN_VALUES.source_agency ? '' : value.source_agency,
  };
}

export interface MetadataFieldsProps {
  value: MetadataFormValue;
  onFieldChange: <K extends MetadataFieldKey>(field: K, next: MetadataFormValue[K]) => void;
  /** Fields whose values differ across the drawer's selection. */
  mixed?: Set<MetadataFieldKey>;
  showRequiredMarks?: boolean;
  /** When true all fields are disabled for editing (read-only view). */
  readOnly?: boolean;
  /** Mode: 'edit' (default for inspector drawer) or 'upload' (for batch upload modal). */
  mode?: 'edit' | 'upload';
  /** Formatted dimensions (e.g. "1920×1080") to display in technical metadata. */
  dimensions?: string | null;
  /**
   * The kinds of asset being edited: one for the upload modal, the distinct
   * kinds of the selection for the drawer. Decides which fields show (the
   * fields every kind owns) and which carry a required mark (the fields every
   * kind requires to publish).
   */
  assetTypes: readonly AssetRules[];
  /**
   * Upload mode only: the source image the batch is being linked to. The fields
   * the backend copies from it are shown read-only from it rather than as
   * selects, so the form does not invite values the server will overwrite.
   */
  derivedFrom?: AssetResponse | null;
  /**
   * Upload mode only: a carousel, whose frames each name a source image. Every
   * field stays an input — what is typed is kept — and the form says that
   * blanks are filled from those sources and the licence cannot be widened
   * past them, which the backend enforces.
   */
  inheritsFromFrames?: boolean;
  /**
   * Edit mode only: the codes of linked source images whose channel rights are
   * still unknown. While there are any the asset's channel rights must stay
   * unknown (the server skips any other answer), so they are shown locked,
   * with where to answer them.
   */
  unknownChannelSources?: readonly string[];
  /** Read-only facts for the technical block (edit mode). "Mixed" is passed through. */
  aspectRatio?: string | null;
  market?: string | null;
  language?: string | null;
}

const CONTROL_CLASS =
  'rounded-md border px-2 py-1.5 text-[12.5px] text-slate-800 outline-none focus:border-[#0014cc] disabled:bg-slate-50/70 disabled:text-slate-600 disabled:cursor-not-allowed';

function controlClass(isMixed: boolean): string {
  return `${CONTROL_CLASS} ${
    isMixed ? 'border-amber-200 bg-amber-50/40 placeholder:italic' : 'border-slate-200'
  }`;
}

export function MetadataFields({
  value,
  onFieldChange,
  mixed,
  showRequiredMarks = true,
  readOnly = false,
  mode = 'edit',
  dimensions,
  assetTypes,
  derivedFrom,
  inheritsFromFrames = false,
  unknownChannelSources = [],
  aspectRatio,
  market,
  language,
}: MetadataFieldsProps) {
  // The native date input holds text the user typed even when that text is
  // not a valid date — its `value` stays '' while the control *looks* filled,
  // which silently fails the completeness check. `validity.badInput` is the
  // only signal that distinguishes "empty" from "filled with something the
  // browser can't parse", so it is surfaced explicitly.
  const [expiryBadInput, setExpiryBadInput] = useState(false);

  const isMixed = (field: MetadataFieldKey) => mixed?.has(field) ?? false;

  const { vocabulary, isError: vocabularyFailed, retry: retryVocabulary } = useVocabulary();

  // What this form shows: the fields every kind in the selection owns. A
  // mixed selection of a master image and a film shows the core set only.
  const visible = intersectOwnedFields(assetTypes);
  const single = assetTypes.length === 1 ? assetTypes[0] : null;

  // "Unknown" is a source image's answer (`UNKNOWN_VALUES`); the server
  // refuses it from any other kind unless a source holds it. So other kinds
  // only see the option while an existing asset holds it, inherited, and can
  // untick it. Never at upload: nothing uploaded has inherited anything yet.
  const picksUnknown = assetTypes.length > 0 && assetTypes.every((type) => type === 'source_image');
  const offerUnknown = (options: TaxonomyOption[], unknown: string, held: boolean) =>
    picksUnknown || (held && mode === 'edit')
      ? options
      : options.filter((option) => option.value !== unknown);
  const channelsLocked = mode === 'edit' && !picksUnknown && unknownChannelSources.length > 0;

  const label = (field: MetadataFieldKey) => (
    <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
      {METADATA_FIELD_LABELS[field]}
      {isPublishRequired(assetTypes, field) && showRequiredMarks && !readOnly && (
        <span className="ml-1 text-amber-500">*</span>
      )}
      {isMixed(field) && (
        <span className="ml-1 rounded bg-amber-100 px-1 py-px text-[9.5px] font-semibold normal-case tracking-normal text-amber-700">
          Mixed
        </span>
      )}
    </span>
  );

  type ListFieldKey =
    | 'nameplate'
    | 'carline_code'
    | 'business_unit'
    | 'tier'
    | 'platform_rights'
    | 'channel_rights'
    | 'country_rights';
  type StringFieldKey = Exclude<
    MetadataFieldKey,
    'copy_elements' | 'is_lifestyle_image' | ListFieldKey
  >;

  const select = (field: StringFieldKey, options: TaxonomyOption[], current: string) => {
    // A stored value the vocabulary no longer lists (a row written before the
    // list was tokenised, e.g. "Ford Blue") stays visible and selectable
    // rather than collapsing to the blank option and silently clearing on save.
    const listed = options.some((option) => option.value === current);
    const shown = current && !listed ? [...options, { value: current, label: current }] : options;
    return (
      <select
        disabled={readOnly}
        value={isMixed(field) && current === '' ? '' : current}
        onChange={(event) => onFieldChange(field, event.target.value)}
        className={controlClass(isMixed(field))}
      >
        <option value="">{isMixed(field) ? 'Mixed' : '—'}</option>
        {shown.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  };

  /**
   * A multi-select rendered as checkboxes; the value is the list of checked
   * tokens. `scrollable` caps the height for the long lists (the countries).
   */
  const checkboxGroup = (
    field: 'business_unit' | 'tier' | 'platform_rights' | 'channel_rights' | 'country_rights',
    options: TaxonomyOption[],
    scrollable = false,
    locked = false,
  ) => {
    const selected = value[field];
    const disabled = readOnly || locked;
    return (
      <div
        role="group"
        aria-label={METADATA_FIELD_LABELS[field]}
        className={`flex flex-wrap gap-x-3 gap-y-1 rounded-md border px-2 py-1.5 ${
          isMixed(field) ? 'border-amber-200 bg-amber-50/40' : 'border-slate-200'
        } ${disabled ? 'bg-slate-50/70' : ''} ${scrollable ? 'max-h-36 overflow-y-auto' : ''}`}
      >
        {options.map((option) => {
          const checked = selected.includes(option.value);
          return (
            <label
              key={option.value}
              className={`flex items-center gap-1 text-[12px] text-slate-700 ${
                disabled ? 'cursor-default' : 'cursor-pointer'
              }`}
            >
              <input
                type="checkbox"
                disabled={disabled}
                checked={checked}
                onChange={() => {
                  if (disabled) return;
                  onFieldChange(
                    field,
                    checked
                      ? selected.filter((entry) => entry !== option.value)
                      : field === 'channel_rights'
                        ? withChannel(selected, option.value)
                        : [...selected, option.value],
                  );
                }}
                className="h-3 w-3 accent-[#0014cc] disabled:cursor-not-allowed"
              />
              {option.label}
            </label>
          );
        })}
      </div>
    );
  };

  const multiSelect = (field: 'nameplate' | 'carline_code', options: TaxonomyOption[]) => (
    <MultiSelectField
      label={METADATA_FIELD_LABELS[field]}
      value={value[field]}
      options={options}
      onChange={(next) => onFieldChange(field, next)}
      mixed={isMixed(field)}
      disabled={readOnly}
    />
  );

  const textInput = (
    field: StringFieldKey,
    placeholder: string,
    maxLength: number,
    type: 'text' | 'url' = 'text',
    list?: string,
  ) => (
    <input
      type={type}
      list={list}
      value={isMixed(field) && value[field] === '' ? '' : value[field]}
      placeholder={isMixed(field) ? 'Mixed' : placeholder}
      maxLength={maxLength}
      disabled={readOnly}
      onChange={(event) => onFieldChange(field, event.target.value)}
      className={controlClass(isMixed(field))}
    />
  );

  const isOlaAsset = single === 'ola';
  // Each block is shown when the kinds own its fields; the registry is the
  // one place that says a film has an FCV id and a hero has no copy.
  const showFilmic = visible.has('legacy_fcv_id');
  const showImageRights = visible.has('expiry_rights') || visible.has('platform_rights');
  const showCopyElements = visible.has('copy_elements');
  const showOlaMaster = visible.has('campaign_name');
  const showFormat = visible.has('format_type');

  // The size list narrows to the sizes the picked type comes in, and the
  // channel is read off the pair — the backend derives it the same way and
  // never accepts one.
  const sizesFor = (type: string) =>
    vocabulary.format_types.find((option) => option.value === type)?.sizes ?? [];
  const formatSizes = sizesFor(value.format_type);
  const formatChannel = formatSizes.find((size) => size.value === value.format_size)?.channel;
  const formatChannelLabel =
    formatChannel &&
    (CHANNEL_OPTIONS.find((o) => o.value === formatChannel)?.label ?? formatChannel);

  // Upload mode with a source image picked: the backend overwrites the fields
  // in `DERIVED_FROM_SOURCE` with the source image's values, so the form shows
  // them from it instead of offering selects whose answer would be discarded.
  const derived = mode === 'upload' ? (derivedFrom ?? null) : null;
  const fromFrames = mode === 'upload' && inheritsFromFrames;
  // Only a single picked source hides the inherited inputs, because the OLA
  // route overwrites them; a carousel keeps what is typed.
  const hidesDerived = derived !== null;

  const campaignListId = useId();
  const campaignSuggestions = useCampaignSuggestions(showOlaMaster);

  const readOnlyRow = (term: string, content: string | null | undefined) => (
    <>
      <dt className="text-slate-500">{term}</dt>
      <dd className="text-slate-800">
        {content === 'Mixed' ? (
          <span className="italic text-amber-600">Mixed</span>
        ) : (
          content || '—'
        )}
      </dd>
    </>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Every select below is fed by the vocabulary fetch. When it fails the
          lists come back empty, and without this the form would just look
          like it has nothing to offer — the same picture as a backend that is
          mid-deploy, a misrouted endpoint, or an auth problem. */}
      {vocabularyFailed && (
        <div
          role="alert"
          className="flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700"
        >
          <span>
            The option lists could not be loaded, so nameplate, tier and the other selects are
            empty.
          </span>
          <button
            type="button"
            onClick={retryVocabulary}
            className="shrink-0 rounded border border-red-300 px-2 py-0.5 text-[11.5px] font-medium hover:bg-red-100"
          >
            Try again
          </button>
        </div>
      )}
      {/* Technical Metadata (Read-Only in Edit Mode) */}
      {mode === 'edit' && (
        <div className="rounded-md border border-slate-200 bg-slate-50/80 p-3">
          <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Technical Metadata
          </h4>

          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[12px]">
            {readOnlyRow('Type', single ? ASSET_RULES[single].label : 'Mixed')}

            {readOnlyRow('Dimensions', dimensions)}
            {aspectRatio && readOnlyRow('Aspect ratio', aspectRatio)}
            {(market || language) && (
              <>
                {readOnlyRow('Market', market)}
                {readOnlyRow('Language', language)}
              </>
            )}
          </dl>
        </div>
      )}

      {/* Copied from the source image (upload mode, one picked) */}
      {derived && (
        <div className="flex flex-col gap-2 rounded-md border border-[#0014cc]/20 bg-[#0014cc]/5 p-3">
          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-[#0014cc]">
            From {derived.source_image_code ?? 'the source image'}
          </h4>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[12px]">
            {readOnlyRow('Nameplate', derived.nameplate?.join(', '))}
            {readOnlyRow('Carline code', derived.carline_code?.join(', '))}
            {readOnlyRow('Business unit', vocabularyLabels(vocabulary, derived.business_unit))}
            {readOnlyRow('Tier', vocabularyLabels(vocabulary, derived.tier))}
            {readOnlyRow('Domain', vocabularyLabel(vocabulary, derived.domain))}
            {readOnlyRow('Channel rights', vocabularyLabels(vocabulary, derived.channel_rights))}
            {readOnlyRow('Country rights', vocabularyLabels(vocabulary, derived.country_rights))}
            {readOnlyRow(
              'Rights expiry',
              derived.expiry_rights ? formatDateDDMMYYYY(derived.expiry_rights) : null,
            )}
          </dl>
          {!derived.expiry_rights && (
            <p className="text-[11.5px] text-amber-700">
              This source image has no usage rights expiry yet. These assets will upload, but cannot
              be published until one is set — on the source image or on each asset.
            </p>
          )}
          <p className="text-[11px] text-slate-500">
            Copied onto every asset in this batch. Editable per asset afterwards.
          </p>
        </div>
      )}

      {fromFrames && (
        <p className="rounded-md border border-[#0014cc]/20 bg-[#0014cc]/5 px-3 py-2 text-[11.5px] text-slate-600">
          Anything left blank is filled from the frames’ source images. Channel rights, country
          rights and expiry can be narrowed, but not set wider than every frame’s source image
          allows; the AI indicator can be raised, but not set below any frame’s source image.
        </p>
      )}

      {isOlaAsset && mode === 'upload' && !hidesDerived && (
        <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11.5px] text-slate-500">
          Pick a source image above to copy nameplate, carline code, business unit, tier, rights
          expiry and creative agency from it — or fill them in here.
        </p>
      )}

      {/* Core Attributes */}
      <div className="flex flex-col gap-3">
        {/* Three independent lists: picking a nameplate suggests nothing for
            the carline code or the business unit, and all three come from the
            source image when one is picked. */}
        {!hidesDerived && (
          <div className="flex flex-col gap-1">
            {label('nameplate')}
            {multiSelect('nameplate', vocabulary.nameplates)}
          </div>
        )}

        {!hidesDerived && (
          <div className="flex flex-col gap-1">
            {label('carline_code')}
            {multiSelect('carline_code', vocabulary.carline_codes)}
          </div>
        )}

        {!hidesDerived && (
          <div className="flex flex-col gap-1">
            {label('business_unit')}
            {checkboxGroup(
              'business_unit',
              offerUnknown(
                vocabulary.business_units,
                UNKNOWN_VALUES.business_unit,
                value.business_unit.includes(UNKNOWN_VALUES.business_unit),
              ),
            )}
          </div>
        )}

        {!hidesDerived && (
          <div className="flex flex-col gap-1">
            {label('tier')}
            {checkboxGroup('tier', vocabulary.tiers)}
          </div>
        )}

        <label className="flex flex-col gap-1">
          {label('ai_indicator')}
          {select('ai_indicator', vocabulary.ai_indicators, value.ai_indicator)}
        </label>

        {visible.has('source_agency') && !hidesDerived && (
          <label className="flex flex-col gap-1">
            {label('source_agency')}
            {select(
              'source_agency',
              offerUnknown(
                vocabulary.source_agencies,
                UNKNOWN_VALUES.source_agency,
                value.source_agency === UNKNOWN_VALUES.source_agency,
              ),
              value.source_agency,
            )}
          </label>
        )}

        {visible.has('domain') && !hidesDerived && (
          <label className="flex flex-col gap-1">
            {label('domain')}
            {select('domain', vocabulary.domains, value.domain)}
          </label>
        )}

        {visible.has('is_lifestyle_image') && (
          <label
            className={`flex items-center gap-2 ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
          >
            <input
              type="checkbox"
              disabled={readOnly}
              checked={value.is_lifestyle_image}
              // A mixed selection shows neither ticked nor unticked; ticking it
              // marks every selected source image.
              ref={(input) => {
                if (input) input.indeterminate = isMixed('is_lifestyle_image');
              }}
              onChange={(event) => onFieldChange('is_lifestyle_image', event.target.checked)}
              className="h-3 w-3 accent-[#0014cc] disabled:cursor-not-allowed"
            />
            {label('is_lifestyle_image')}
          </label>
        )}
      </div>

      {/* Usage rights: by channel and by market. Every kind owns them; only a
          source image owes them to publish. Inherited onto derivatives when
          they are linked (so hidden here while a source image is picked, like
          the other inherited fields), and editable afterwards so a crop can
          narrow what the hero was cleared for. */}
      {visible.has('channel_rights') && !hidesDerived && (
        <div className="flex flex-col gap-3 border-t border-slate-100 pt-3">
          <h4 className="text-[11.5px] font-semibold uppercase tracking-wider text-slate-700">
            Usage rights
          </h4>

          <div className="flex flex-col gap-1">
            {label('channel_rights')}
            {checkboxGroup(
              'channel_rights',
              offerUnknown(
                vocabulary.channel_rights,
                UNKNOWN_VALUES.channel_rights,
                value.channel_rights.includes(UNKNOWN_VALUES.channel_rights),
              ),
              false,
              channelsLocked,
            )}
            {channelsLocked && !readOnly && (
              <p className="text-[11px] text-slate-500">
                Unknown on source image {unknownChannelSources.join(', ')}. Answer them there.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            {label('country_rights')}
            {checkboxGroup(
              'country_rights',
              // Regions first: picking Pan-Euro is the common case and should not
              // sit below nineteen countries.
              [...vocabulary.regions, ...vocabulary.countries],
              true,
            )}
          </div>
        </div>
      )}

      {/* Master image format: the type and ratio it was cut for, and the
          channel that pair runs in. */}
      {showFormat && (
        <div className="flex flex-col gap-3 border-t border-slate-100 pt-3">
          <h4 className="text-[11.5px] font-semibold uppercase tracking-wider text-slate-700">
            Format
          </h4>

          <label className="flex flex-col gap-1">
            {label('format_type')}
            <select
              disabled={readOnly}
              value={value.format_type}
              onChange={(event) => {
                const next = event.target.value;
                onFieldChange('format_type', next);
                // A size the new type does not come in would be refused.
                if (!sizesFor(next).some((size) => size.value === value.format_size)) {
                  onFieldChange('format_size', '');
                }
              }}
              className={controlClass(isMixed('format_type'))}
            >
              <option value="">{isMixed('format_type') ? 'Mixed' : '—'}</option>
              {/* Never Carousel: that format is set by uploading the stills as
                  a carousel, and the backend refuses it from a picker. */}
              {vocabulary.format_types
                .filter((option) => option.value !== CAROUSEL_FORMAT)
                .map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            {label('format_size')}
            <select
              disabled={readOnly || formatSizes.length === 0}
              value={value.format_size}
              onChange={(event) => onFieldChange('format_size', event.target.value)}
              className={controlClass(isMixed('format_size'))}
            >
              <option value="">
                {isMixed('format_size')
                  ? 'Mixed'
                  : formatSizes.length > 0
                    ? '—'
                    : 'Pick a format type first'}
              </option>
              {formatSizes.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[12px]">
            {readOnlyRow(
              'Channel',
              isMixed('format_type') || isMixed('format_size') ? 'Mixed' : formatChannelLabel,
            )}
            <dd className="col-span-2 text-[11px] text-slate-400">
              Set from the format type and size.
            </dd>
          </dl>
        </div>
      )}

      {/* OLA master asset */}
      {showOlaMaster && (
        <div className="flex flex-col gap-3 border-t border-slate-100 pt-3">
          <h4 className="text-[11.5px] font-semibold uppercase tracking-wider text-slate-700">
            OLA master
          </h4>

          <label className="flex flex-col gap-1">
            {label('campaign_name')}
            {textInput('campaign_name', 'e.g. Puma Gen-E Launch', 200, 'text', campaignListId)}
            <datalist id={campaignListId}>
              {campaignSuggestions.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </label>

          <label className="flex flex-col gap-1">
            {label('creative_variation')}
            {textInput('creative_variation', 'V1, V2 … (template variant)', 50)}
          </label>

          <label className="flex flex-col gap-1">
            {label('production_ticket_ref')}
            {textInput('production_ticket_ref', 'JIRA master ticket', 100)}
          </label>

          <label className="flex flex-col gap-1">
            {label('briefing_form_ref')}
            {textInput('briefing_form_ref', 'https://…', 1000, 'url')}
          </label>

          <label className="flex flex-col gap-1">
            {label('notes')}
            <textarea
              rows={2}
              placeholder={isMixed('notes') ? 'Mixed' : 'Anything the next person should know'}
              value={isMixed('notes') && value.notes === '' ? '' : value.notes}
              disabled={readOnly}
              onChange={(event) => onFieldChange('notes', event.target.value)}
              className={controlClass(isMixed('notes'))}
            />
          </label>

          {mode === 'upload' && (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[12px]">
              {readOnlyRow('Market', OLA_MARKET)}
              {readOnlyRow('Language', OLA_LANGUAGE)}
              <dd className="col-span-2 text-[11px] text-slate-400">
                Masters are always EU / English; localised files are not uploaded here.
              </dd>
            </dl>
          )}
        </div>
      )}

      {/* Filmic: what the uploader declares about the film (nothing is read
          from the video), and the FCV 1.0 id. A film's rights are the shared
          expiry below. */}
      {showFilmic && (
        <div className="flex flex-col gap-3 border-t border-slate-100 pt-3">
          <label className="flex flex-col gap-1">
            {label('format_size')}
            <select
              disabled={readOnly}
              value={value.format_size}
              onChange={(event) => onFieldChange('format_size', event.target.value)}
              className={controlClass(isMixed('format_size'))}
            >
              <option value="">{isMixed('format_size') ? 'Mixed' : '—'}</option>
              {vocabulary.film_format_sizes.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            {label('duration_seconds')}
            <select
              disabled={readOnly}
              value={value.duration_seconds}
              onChange={(event) => onFieldChange('duration_seconds', event.target.value)}
              className={controlClass(isMixed('duration_seconds'))}
            >
              <option value="">{isMixed('duration_seconds') ? 'Mixed' : '—'}</option>
              {vocabulary.film_durations.map((seconds) => (
                <option key={seconds} value={String(seconds)}>
                  {filmDurationLabel(seconds)}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            {label('legacy_fcv_id')}
            {textInput('legacy_fcv_id', 'FCV 1.0 id for tracking', 100)}
          </label>
        </div>
      )}

      {/* Image rights (headed "Rights" for an OLA or a film) */}
      {showImageRights && (
        <div className="flex flex-col gap-3 border-t border-slate-100 pt-3">
          <h4 className="text-[11.5px] font-semibold uppercase tracking-wider text-slate-700">
            {isOlaAsset || single === 'filmic' ? 'Rights' : 'Image rights'}
          </h4>

          {!hidesDerived && (
            <label className="flex flex-col gap-1">
              {label('expiry_rights')}
              <input
                type="date"
                disabled={readOnly}
                readOnly={readOnly}
                value={
                  isMixed('expiry_rights') && value.expiry_rights === '' ? '' : value.expiry_rights
                }
                onChange={(event) => {
                  if (readOnly) return;
                  onFieldChange('expiry_rights', event.target.value);
                  setExpiryBadInput(event.target.validity?.badInput ?? false);
                }}
                onBlur={(event) => setExpiryBadInput(event.target.validity?.badInput ?? false)}
                className={controlClass(isMixed('expiry_rights'))}
              />
              {expiryBadInput && !readOnly && (
                <p className="text-[11px] text-red-600">
                  This isn't a complete date yet — finish it or pick one from the calendar.
                </p>
              )}
            </label>
          )}

          {/* A hero is licensed by channel (above), not by platform. */}
          {visible.has('platform_rights') && (
            <div className="flex flex-col gap-1">
              {label('platform_rights')}
              {checkboxGroup('platform_rights', PLATFORM_RIGHTS_OPTIONS)}
            </div>
          )}

          {showCopyElements && (
            // Marked for `focusCopy`, which a caption's "Add copy" calls.
            <div data-copy-field className="flex flex-col gap-1">
              {label('copy_elements')}
              <CopyElementsEditor
                value={value.copy_elements}
                onChange={(rows) => onFieldChange('copy_elements', rows)}
                isMixed={isMixed('copy_elements')}
                readOnly={readOnly}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
